import { ConfigProvider, Modal as AntModal } from 'antd'
import { ReactNode } from 'react'
import styled from 'styled-components'

import { JustChildren } from 'components/common/react-types'
import { newPalette } from 'components/constants/colors'
import CloseSvg from 'components/images/icons/close.svg'

// The dialog is portalled to `document.body`, so it sits outside any wrapper we could hang
// descendant selectors off. Colours therefore come from Ant's component tokens, the same way
// `input/input-design-config.tsx` scopes the theming for input components.
const modalTheme = {
  token: {
    // Radius of the dialog panel, matching the cards it opens over.
    borderRadiusLG: 18,

    // Body copy colour, matching `StyledParagraph`.
    colorText: newPalette.gray[300],
  },

  components: {
    Modal: {
      // The modal is an elevated surface, so it reuses the background already given to Ant's other
      // elevated surfaces (`colorBgElevated` in `ant-design-config.tsx`).
      contentBg: newPalette.panelBackground,
      headerBg: newPalette.panelBackground,
      footerBg: newPalette.panelBackground,

      // Title styling, matching `H3Font` in `components/common/title.tsx`.
      titleColor: newPalette.whiteAlpha[95],
      titleFontSize: 20,
      titleLineHeight: 1.5,
    },
  },
}

// Ant writes its own rules at the same specificity as ours and the injection order between its
// CSS-in-JS and styled-components is not deterministic, so `&&` doubles the generated class to
// make sure these win.
const StyledModal = styled(AntModal)`
  && .ant-modal-header {
    padding: 24px 24px 0;
    border-bottom: none;
  }

  && .ant-modal-body {
    padding: 12px 24px 24px;
  }

  && .ant-modal-footer {
    padding: 0 24px 24px;
    margin-top: 0;
    border-top: none;
  }

  && .ant-modal-close {
    top: 24px;
    inset-inline-end: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    padding: 2px;
    border-radius: 4px;
  }

  && .ant-modal-close:hover {
    background: ${newPalette.whiteAlpha[10]};
  }

  && .ant-modal-close:focus-visible {
    outline: 2px solid ${newPalette.whiteAlpha[40]};
  }
`

function ModalDesignConfig({ children }: JustChildren): JSX.Element {
  return <ConfigProvider theme={modalTheme}>{children}</ConfigProvider>
}

export interface ModalProps {
  isOpen: boolean
  title: string
  children: ReactNode

  // Optional action row. Nothing renders when it is omitted, which leaves the button layout to the
  // caller.
  footer?: ReactNode

  // Fires for every dismissal route: the close button, Escape, and clicking the mask.
  onClose(): void
}

export function Modal({ isOpen, title, children, footer, onClose }: ModalProps): JSX.Element {
  return (
    <ModalDesignConfig>
      <StyledModal
        open={isOpen}
        onCancel={onClose}
        title={title}
        // Ant renders the close affordance as an already-labelled button and treats `closeIcon` as
        // its contents, so this is the brand icon inside an accessible button.
        closeIcon={<img src={CloseSvg} alt="" draggable={false} />}
        footer={footer ?? null}
        centered
        destroyOnClose
      >
        {children}
      </StyledModal>
    </ModalDesignConfig>
  )
}
